import { SpeechError, SpeechErrorCode } from '../errors'
import { fetchWithTimeout, toScore, vendorHttpError } from '../utils'

const VENDOR = 'azure'

/** base64(UTF-8)。参考文本含中文，直接 btoa 会抛 InvalidCharacterError。 */
function base64Utf8(str) {
  const bytes = new TextEncoder().encode(str)
  let binary = ''
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i])
  return btoa(binary)
}

/** 从音素串解析声调："hao 3" → 3；拿不到返回 undefined */
function toneFromPhoneme(phoneme) {
  if (typeof phoneme !== 'string') return undefined
  const m = phoneme.match(/\s(\d)\s*$/)
  return m ? Number(m[1]) : undefined
}

/**
 * 把 Azure 的词级结果摊平成逐字。
 * 中文一个 Word 可能是多字词（"今天"），而题面是按字排的，
 * 所以优先用 Syllables（Grapheme 就是单个汉字），并用同下标的 Phonemes 取声调。
 */
function flattenWords(words) {
  const out = []
  for (const w of words || []) {
    const syllables = w.Syllables || []
    const phonemes = w.Phonemes || []
    if (syllables.length) {
      syllables.forEach((s, i) => {
        out.push({
          text: s.Grapheme || '',
          score: toScore(s.AccuracyScore),
          errorType: w.ErrorType,
          tone: toneFromPhoneme(phonemes[i] && phonemes[i].Phoneme),
          // Azure 不单独给声调得分，只有声调号
          toneScore: undefined,
        })
      })
    } else {
      out.push({
        text: w.Word || '',
        score: toScore(w.AccuracyScore),
        errorType: w.ErrorType,
        tone: undefined,
        toneScore: undefined,
      })
    }
  }
  return out
}

/**
 * Azure 发音评估（短音频 REST 接口）。
 *
 * 官方文档（rest-speech-to-text-short）：发音评估的音频不能超过 30 秒，
 * 这是接口硬限制，录音时长上限见 credentials.js 的 maxDurationMsFor。
 *
 * @param {Blob} wav 16kHz / 16bit / 单声道 WAV
 * @param {string} refText 参考文本
 * @param {'word'|'sentence'|'paragraph'} category 题型
 * @param {object} credential capability=eval、protocol=http 下发的 credential（vendor=azure）
 * @param {number} [timeoutMs]
 * @returns {Promise<object>} 归一化 EvalResult
 * @throws {SpeechError} NO_ENDPOINT / VENDOR_REJECTED / VENDOR_FAILED / BAD_RESPONSE / NETWORK / TIMEOUT
 */
// 实测 30 秒音频 Azure 要跑 8~10 秒，再加上传，20 秒偏紧
export async function assess({ wav, refText, category, credential, timeoutMs = 30000 }) {
  const endpoint = (credential && credential.endpoint) || {}
  const payload = (credential && credential.payload) || {}
  const extra = (credential && credential.extra) || {}

  if (!endpoint.host || !endpoint.path) {
    throw new SpeechError(SpeechErrorCode.NO_ENDPOINT, 'azure credential has no endpoint', {
      vendor: VENDOR,
    })
  }
  if (!payload.token) {
    throw new SpeechError(SpeechErrorCode.NO_ENDPOINT, 'azure credential has no token', {
      vendor: VENDOR,
    })
  }

  const url = `https://${endpoint.host}${endpoint.path}?language=zh-CN&format=detailed`

  // 一律关掉韵律评分。实测（参考音频用 Azure TTS 合成，即"满分朗读"）：
  //   参考文本   韵律开 PronScore   韵律关 PronScore
  //   好          58.2              94.6
  //   你好        59                97.6
  //   中文书      83.4              97.6
  //   今天天气很好 88.5              98.8
  // 开着韵律时 1~2 字的词连满分音频都到不了 60 分（判定线就是 60），单字题会全判错；
  // 而且它让 Azure 与 SpeechSuper（同样的 60 分线）的分数分布差出一大截。
  // 页面只消费总分，韵律本来也不是被替换掉的旧方案里的维度，关掉才是功能对齐。
  const isWord = category === 'word'
  const paParams = {
    ReferenceText: refText || '',
    GradingSystem: 'HundredMark',
    Granularity: 'Phoneme',
    Dimension: 'Comprehensive',
    // 单字题没有漏读/多读的概念，开着反而容易误判
    EnableMiscue: !isWord,
    EnableProsodyAssessment: false,
  }

  const headers = {
    [payload.header || 'Authorization']: `${payload.scheme || 'Bearer'} ${payload.token}`,
    'Content-Type': 'audio/wav; codecs=audio/pcm; samplerate=16000',
    Accept: 'application/json',
    [extra.pronunciationAssessmentHeader || 'Pronunciation-Assessment']: base64Utf8(
      JSON.stringify(paParams)
    ),
  }

  const startedAt = Date.now()
  const { res, parsed } = await fetchWithTimeout(
    url,
    { method: 'POST', headers, body: wav, mode: 'cors', credentials: 'omit' },
    timeoutMs,
    { vendor: VENDOR }
  )
  const latencyMs = Date.now() - startedAt

  if (!res.ok) throw vendorHttpError(res, parsed, VENDOR)

  const raw = parsed.json
  if (!raw) {
    throw new SpeechError(SpeechErrorCode.BAD_RESPONSE, 'azure returned non-JSON body', {
      vendor: VENDOR,
      body: parsed.text.slice(0, 500),
    })
  }

  const status = raw.RecognitionStatus
  const best = (raw.NBest && raw.NBest[0]) || null

  // 没听到有效语音是合法结果，不是错误——调用方要靠这个 0 分收尾
  if (status !== 'Success' || !best) {
    return {
      vendor: VENDOR,
      totalScore: 0,
      accuracyScore: undefined,
      fluencyScore: undefined,
      completenessScore: undefined,
      recognizedText: raw.DisplayText || '',
      words: [],
      recognitionStatus: status || 'NoResult',
      raw,
      requestId: undefined,
      latencyMs,
    }
  }

  const totalScore = toScore(best.PronScore)
  if (totalScore === undefined) {
    throw new SpeechError(SpeechErrorCode.BAD_RESPONSE, 'azure response has no PronScore', {
      vendor: VENDOR,
    })
  }

  return {
    vendor: VENDOR,
    totalScore,
    accuracyScore: toScore(best.AccuracyScore),
    fluencyScore: toScore(best.FluencyScore),
    completenessScore: toScore(best.CompletenessScore),
    recognizedText: best.Display || raw.DisplayText || '',
    words: flattenWords(best.Words),
    recognitionStatus: status,
    raw,
    // Azure 没有 access-control-expose-headers，浏览器读不到任何厂商请求 ID
    requestId: undefined,
    latencyMs,
  }
}
